/**
 * Render engine: drives the replayer's tick clock against a sample clock.
 *
 * Shared by the AudioWorklet and the offline WAV renderer, so what you export is
 * bit-identical to what you heard.
 */
import { Mixer, type MixerSettings } from './dsp/mixer';
import { Player, type PlayerOptions } from './player/player';
import type { Song } from './types';

export interface EngineOptions {
  player?: Partial<PlayerOptions>;
  mixer?: Partial<MixerSettings>;
}

export class RenderEngine {
  player: Player;
  mixer: Mixer;
  playing = false;

  /**
   * Frames still owed to the current tick. Kept fractional so tempos that do not
   * divide the sample rate evenly do not drift — at 125 BPM and 44.1 kHz a tick
   * is 882 frames exactly, but at 143 BPM it is 770.98, and rounding that every
   * tick would run measurably fast over a few minutes.
   */
  private tickRemainder = 0;

  constructor(song: Song, sampleRate: number, options: EngineOptions = {}) {
    this.player = new Player(song, sampleRate, options.player);
    this.mixer = new Mixer(
      sampleRate,
      song.channels + this.player.previewChannels.length,
      512,
      options.mixer,
    );
  }

  setSong(song: Song): void {
    this.player.setSong(song);
    this.mixer = new Mixer(
      this.player.sampleRate,
      song.channels + this.player.previewChannels.length,
      512,
      this.mixer.settings,
    );
    this.tickRemainder = 0;
  }

  setSampleRate(rate: number): void {
    this.player.sampleRate = rate;
    this.mixer.setSampleRate(rate);
  }

  play(): void {
    this.playing = true;
  }

  pause(): void {
    this.playing = false;
    this.player.allNotesOff();
  }

  stop(): void {
    this.playing = false;
    this.player.reset();
    this.mixer.reset();
    this.tickRemainder = 0;
  }

  /**
   * Fill `frames` of stereo output.
   *
   * The loop walks tick boundaries rather than rendering the whole block at
   * once, because every replayer parameter — pitch, volume, panning — is only
   * allowed to change on a tick. Rendering across a boundary would apply the new
   * tick's values to audio that belongs to the old one.
   */
  process(outL: Float32Array, outR: Float32Array, frames: number): void {
    let done = 0;

    while (done < frames) {
      if (this.tickRemainder <= 0) {
        if (this.playing) {
          this.player.doTick();
          this.mixer.setLedFilter(this.player.ledFilter);
        } else {
          // Stopped, but keyboard previews still need their envelopes ticked.
          this.player.tickPreview();
        }
        this.tickRemainder += this.player.samplesPerTick;
      }

      const chunk = Math.max(1, Math.min(frames - done, Math.ceil(this.tickRemainder)));
      this.mixer.render(this.player.renderChannels, outL, outR, done, chunk);
      this.tickRemainder -= chunk;
      done += chunk;
    }
  }

  /** Snapshot for the UI. */
  getState() {
    return {
      playing: this.playing,
      order: this.player.order,
      pattern: this.player.pattern,
      row: this.player.row,
      tick: this.player.tick,
      speed: this.player.speed,
      tempo: this.player.tempo,
      globalVolume: this.player.globalVolume,
      songEnded: this.player.songEnded,
      loopCount: this.player.loopCount,
    };
  }
}

export type EngineState = ReturnType<RenderEngine['getState']>;
